import {
  ChecklistFormValues,
  NetworkNodeFieldPath,
  NetworkNodeFormValues,
  ShiftFormValues,
} from '../../../types';
import {
  BlockActionResult,
  formatUsageScreensMessage,
  getNodeAtPath,
} from '../../networkReferenceUtils';

export type ShiftUsage = {
  checklists: ChecklistFormValues[];
};

export function findShiftUsage(
  network: NetworkNodeFormValues[],
  nodePath: NetworkNodeFieldPath,
  shiftId: string,
): ShiftUsage {
  const node = getNodeAtPath(network, nodePath);
  const checklists = (node?.checklists ?? []) as ChecklistFormValues[];

  return {
    checklists: checklists.filter(checklist => checklist.shiftId === shiftId),
  };
}

export function isShiftInUse(usage: ShiftUsage): boolean {
  return usage.checklists.length > 0;
}

const getShiftUsageScreens = (usage: ShiftUsage): string[] =>
  usage.checklists.length > 0 ? ["צ'קליסטים"] : [];

export function formatShiftDeleteBlockMessage(
  shiftName: string,
  usage: ShiftUsage,
): string {
  const screens = formatUsageScreensMessage(getShiftUsageScreens(usage));
  return `לא ניתן למחוק את המשמרת "${shiftName || 'ללא שם'}" — היא בשימוש במסכי ${screens}. יש להסיר את השיוכים תחילה`;
}

export function formatShiftReplaceBlockMessage(
  inUse: Array<{shift: ShiftFormValues; usage: ShiftUsage}>,
): string {
  const screens = new Set<string>();
  inUse.forEach(({usage}) => {
    getShiftUsageScreens(usage).forEach(screen => screens.add(screen));
  });

  const shiftNames = inUse
    .map(({shift}) => `"${shift.name?.trim() || 'ללא שם'}"`)
    .join(', ');
  return `לא ניתן לייבא, המשמרות ${shiftNames} בשימוש במסכי ${formatUsageScreensMessage([...screens])}. יש להסיר את השיוכים תחילה`;
}

export function getInUseShiftsOnNode(
  network: NetworkNodeFormValues[],
  nodePath: NetworkNodeFieldPath,
): Array<{shift: ShiftFormValues; usage: ShiftUsage}> {
  const node = getNodeAtPath(network, nodePath);
  if (!node) return [];

  return (node.shifts ?? [])
    .map(shift => ({
      shift,
      usage: findShiftUsage(network, nodePath, shift.id),
    }))
    .filter(({usage}) => isShiftInUse(usage));
}

export function canDeleteShift(
  network: NetworkNodeFormValues[],
  nodePath: NetworkNodeFieldPath,
  shiftId: string,
  shiftName: string,
): BlockActionResult {
  const usage = findShiftUsage(network, nodePath, shiftId);
  if (isShiftInUse(usage)) {
    return {
      allowed: false,
      message: formatShiftDeleteBlockMessage(shiftName, usage),
    };
  }

  return {allowed: true};
}

export function canReplaceShifts(
  network: NetworkNodeFormValues[],
  nodePath: NetworkNodeFieldPath,
): BlockActionResult {
  const inUse = getInUseShiftsOnNode(network, nodePath);

  if (inUse.length === 0) {
    return {allowed: true};
  }

  return {
    allowed: false,
    message: formatShiftReplaceBlockMessage(inUse),
  };
}
